/**
 * Types for the Business Support surface.
 *
 * Mirrors the backend DTOs in `com.reverse.job.portal.Support.DTO`. The channel keys are the
 * canonical `utm_source` values written by the attribution layer, which is why they are
 * lower-case strings rather than the enum names - the frontend must filter on the same value
 * that ends up in `visitor_session.first_source`, or a tab will silently show nothing.
 */

/** Tab keys. Must match `SupportChannel.utmSource()` on the backend. */
export type SupportChannelKey = 'linkedin' | 'whatsapp' | 'email' | 'telegram' | 'direct';

/**
 * Whether support can actually reach someone on this channel.
 *
 * Only email is true, and only when the visitor has registered - an unregistered visitor has no
 * address at all. The UI branches on this to offer "send" versus "copy and send yourself", so
 * getting it wrong means either a button that does nothing or a nudge nobody receives.
 */
export interface SupportChannel {
  channel: SupportChannelKey;
  label: string;
  visitors: number;
  registrationStarted: number;
  registered: number;
  /** Arrived but never completed registration. The population worth chasing. */
  notRegistered: number;
  approved: number;
  conversionPct: number;
  /** False when no traffic has ever been attributed here. The card still renders. */
  hasTraffic: boolean;
  canSend: boolean;
  /** True when the backend can transmit without an address on file. False for every channel today. */
  providerIntegrated: boolean;
}

export interface SupportVisitor {
  visitorId: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  landingPage: string | null;
  referrer: string | null;
  pagesViewed: number | null;
  sessionCount: number | null;
  deviceCategory: string | null;
  country: string | null;
  registrationStarted: boolean;
  registered: boolean;
  /**
   * Null for exactly the people worth chasing. Its absence is the signal that this visitor
   * cannot be emailed, so the UI offers a copyable message instead of a send button.
   */
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  status: string | null;
  role: string | null;
  /**
   * Status of the visitor's most recent outreach, or null when they have never been nudged.
   * An untouched row is the one worth chasing; a status means the row has already been worked.
   */
  lastOutreachStatus: 'LOGGED' | 'SENT' | 'FAILED' | 'SHARED' | null;
  contactable?: boolean;
  fullName?: string | null;
}

export interface SupportLink {
  id: string;
  /**
   * Admin-facing label, and the value shared with recipients: verbatim when it is a full
   * http(s) URL, otherwise it names the short link built from it. See `shareableLink`.
   */
  displayName: string;
  /** Tab this link is filed under. Independent of `utmSource` by design. */
  channel: SupportChannelKey | null;
  baseUrl: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  /** The shareable URL with UTM applied. This is the string to copy. */
  url: string | null;
  /**
   * Short shareable form: `{site}/l/{slug}`. Clicking it redirects to `url`, so a message
   * carries a tidy address while tracking still sees the full UTM set. Null only for rows
   * created before short slugs existed and not yet backfilled.
   */
  shortUrl: string | null;
  /** Copy to paste alongside the link. `{link}` is substituted at send time. */
  messageTemplate: string | null;
  active: boolean;
  createdBy: string | null;
  createdAt: string | null;
  visitors: number;
  registrationStarted: number;
  registered: number;
  approved: number;
  conversionPct: number;
  outreachCount: number;
  /**
   * True when another link shares this campaign and content pair. Analytics match on the UTM
   * pair rather than the link id, so both links report the same numbers - shown as a warning
   * rather than left for the admin to work out from two identical rows.
   */
  sharedCampaign: boolean;
}

export interface SupportLinkRequest {
  id?: string;
  displayName: string;
  channel: SupportChannelKey;
  baseUrl: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  messageTemplate?: string | null;
  active?: boolean;
}

export interface SupportOutreach {
  id: string;
  supportLinkId: string | null;
  /** Null when the referenced link has since been deleted. */
  linkDisplayName: string | null;
  channel: SupportChannelKey | null;
  visitorId: string | null;
  message: string | null;
  /** LOGGED | SENT | FAILED | SHARED */
  status: 'LOGGED' | 'SENT' | 'FAILED' | 'SHARED';
  error: string | null;
  sentBy: string | null;
  createdAt: string | null;
  recipientEmail: string | null;
}

export interface SupportOutreachRequest {
  linkId?: string | null;
  channel: SupportChannelKey;
  visitorId?: string | null;
  message?: string | null;
  attemptSend?: boolean;
}

/**
 * Outcome of recording a nudge.
 *
 * `delivered` is what the UI branches on, and it is separate from `status` on purpose. `LOGGED`
 * is not a failure: the admin sent the message themselves and we recorded it, which is the
 * normal path for an unregistered visitor. A 200 response does not mean the message was
 * transmitted.
 */
export interface SupportOutreachResult {
  outreachId: string;
  status: 'LOGGED' | 'SENT' | 'FAILED' | 'SHARED';
  delivered: boolean;
  /** The composed message with the link substituted in. Always returned. */
  message: string | null;
  recipientEmail: string | null;
  /** True when there was no address, which is why this was recorded rather than sent. */
  manualOnly: boolean;
  reason: string | null;
  error: string | null;
}

export interface SupportChannelFilters {
  fromDate?: string;
  toDate?: string;
  /** Narrows to one tab. Accepts a channel key or the enum-style name. */
  source?: SupportChannelKey | null;
}

export interface SupportVisitorFilters extends SupportChannelFilters {
  source?: SupportChannelKey | null;
  /** Exact campaign key. Feeds the quick-filter dropdown. */
  campaign?: string | null;
  search?: string;
  notRegisteredOnly?: boolean;
  page?: number;
  size?: number;
}

/**
 * Who a bulk nudge reaches. Exactly one of `visitorIds` and `allMatching` must be present.
 * `allMatching` re-uses the tab's filters so a selected campaign can be swept in one go.
 */
export interface SupportBulkOutreachRequest {
  linkId: string;
  channel: SupportChannelKey;
  message?: string | null;
  /** Explicit list. Mutually exclusive with `allMatching`. */
  visitorIds?: string[];
  /** Re-run the tab's filter against the backend. Mutually exclusive with `visitorIds`. */
  allMatching?: {
    source?: SupportChannelKey | null;
    campaign?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    search?: string | null;
    notRegisteredOnly?: boolean;
  } | null;
}

export interface SupportBulkOutreachFailure {
  visitorId: string;
  error: string;
}

export interface SupportBulkOutreachResult {
  total: number;
  sent: number;
  shared: number;
  logged: number;
  failed: number;
  failures: SupportBulkOutreachFailure[];
}

export interface SupportLinkFilters extends SupportChannelFilters {
  channel?: SupportChannelKey | null;
  search?: string;
  active?: boolean | null;
}

/** Tab order. Generate Link leads because it is where a new campaign starts. */
export const SUPPORT_CHANNELS: SupportChannelKey[] = [
  'linkedin',
  'whatsapp',
  'email',
  'telegram',
  'direct',
];

export const SUPPORT_CHANNEL_LABELS: Record<SupportChannelKey, string> = {
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  email: 'Email',
  telegram: 'Telegram',
  direct: 'Direct',
};
